import React from "react";
import { Link } from "react-router-dom";

import { AlertTriangle, Clock } from "@/components/icons";
import type { LakeStatusResponse, TelemetryLakeMeta } from "@/lib/telemetry";
import {
  describeLag,
  formatSeconds,
  formatTs,
  type LakeLag,
  type TelemetryTz,
} from "@/components/telemetry/format";

/**
 * Freshness of the lake, stated rather than implied.
 *
 * The lake is minutes-to-hours behind by design, so "no rows in the last hour"
 * is not the same claim as "the charger was silent". Everything here exists so
 * that distinction stays visible: the watermark, whether the feeder's own
 * status object could be read at all, and how far behind it is.
 */
export function LakeFreshness({
  lake,
  status,
  tz,
  unitId,
}: {
  lake: TelemetryLakeMeta | null;
  /** Admin-only extra detail from /lake/status. Absent for everyone else. */
  status?: LakeStatusResponse | null;
  tz: TelemetryTz;
  unitId: string;
}) {
  const watermark = lake?.watermark_ts ?? status?.watermark_ts ?? null;
  const lag = lake?.lag_seconds ?? status?.lag_seconds ?? null;
  const available = lake?.status_available ?? status?.available ?? null;
  const stale = lake?.status_stale ?? status?.stale ?? null;
  const pending = lake?.pending_files
    ?? (typeof status?.backlog?.pending_files === "number" ? status.backlog.pending_files : null);

  const freshnessUnknown = available === false;
  const lagInfo = describeLag(lag);

  return (
    <div
      data-testid="telemetry-lake-freshness"
      className="flex flex-wrap items-center gap-x-4 gap-y-1 rounded-lg border border-border bg-muted/30 px-3 py-2 text-xs"
    >
      <span className="flex items-center gap-1.5 font-medium">
        <Clock className="h-3.5 w-3.5" />
        Lake watermark
      </span>

      {freshnessUnknown ? (
        <span className="text-amber-600 dark:text-amber-400" data-testid="telemetry-freshness-unknown">
          Feeder status unavailable
          {status?.reason ? ` (${status.reason})` : ""} — freshness unknown, empty hours are
          reported as “unknown” rather than “no data”.
        </span>
      ) : (
        <>
          <span className="font-mono">{formatTs(watermark, tz)}</span>
          <span
            className={lagClass(lagInfo)}
            data-testid="telemetry-lake-lag"
            data-lag-kind={lagInfo.kind}
          >
            {lagInfo.text}
          </span>
          {stale && (
            <span className="text-amber-600 dark:text-amber-400" data-testid="telemetry-status-stale">
              feeder status is stale
              {status?.status_age_seconds != null
                ? ` (${formatSeconds(status.status_age_seconds)} old)`
                : ""}
            </span>
          )}
        </>
      )}

      {pending != null && pending > 0 && (
        <span data-testid="telemetry-backlog">Archiving in progress — {pending} files pending</span>
      )}

      {lake?.unit_bucket && (
        <span className="opacity-60">
          bucket {lake.unit_bucket}/{lake.unit_bucket_count}
        </span>
      )}
      {lake && lake.sampling_rate > 1 && (
        <span className="opacity-60">sampling 1/{lake.sampling_rate}</span>
      )}

      <Link
        to={`/logs?unit_id=${encodeURIComponent(unitId)}`}
        className="ml-auto text-primary hover:underline"
      >
        Live logs (Redis) →
      </Link>
    </div>
  );
}

function lagClass(lag: LakeLag): string {
  // A watermark in the future is a broken feeder, not a slow one — it never
  // reads as muted detail.
  if (lag.kind === "future") return "font-medium text-destructive";
  if (lag.kind === "behind" && lag.behindSeconds > 3600) {
    return "text-amber-600 dark:text-amber-400";
  }
  return "text-muted-foreground";
}

/**
 * The one banner that must never be quiet: the backend's lake layout disagrees
 * with what the feeder actually wrote, so queries would be valid SQL against
 * partitions no object is in — every row missing, nothing failing.
 */
export function LakeConfigMismatch({ detail }: { detail: Record<string, unknown> }) {
  const expected = detail.expected as Record<string, unknown> | undefined;
  const found = detail.found as Record<string, unknown> | undefined;
  const mismatch = detail.mismatch as Record<string, unknown> | undefined;

  return (
    <div
      data-testid="telemetry-config-mismatch"
      className="rounded-lg border-2 border-destructive bg-destructive/10 p-4 text-sm"
    >
      <div className="flex items-center gap-2 font-semibold text-destructive">
        <AlertTriangle className="h-4 w-4" />
        Telemetry lake configuration mismatch — reporting is disabled
      </div>
      <p className="mt-1 text-destructive/90">
        The backend’s lake configuration does not match what the feeder wrote. Queries would
        return an empty answer that looks correct. Reconcile the backend config with the
        feeder before trusting anything on this page.
      </p>
      <dl className="mt-2 grid grid-cols-[auto_1fr] gap-x-4 gap-y-0.5 font-mono text-xs">
        {Object.entries(mismatch ?? {}).map(([key, value]) => (
          <React.Fragment key={key}>
            <dt className="opacity-70">{key}</dt>
            <dd>{JSON.stringify(value)}</dd>
          </React.Fragment>
        ))}
        {!mismatch && expected && (
          <>
            <dt className="opacity-70">expected</dt>
            <dd>{JSON.stringify(expected)}</dd>
            <dt className="opacity-70">found</dt>
            <dd>{JSON.stringify(found ?? {})}</dd>
          </>
        )}
      </dl>
    </div>
  );
}
